import {IReasonRejectionsDict} from './../../shared/types/reasonRejectionsDict.types';
import qs from 'qs';

import axiosInstance from '../protected';

import {IPageable, IPageableDto} from '../../shared/types/pageable.types';
import {IDeviceTypesDict, IDeviceTypesDictPostBody} from '../../shared/types/deviceTypesDict.types';

import {getDevicesUrl, getDictionaryUrl, getModelsUrl} from '../api.config';
import {ITaskLocationsDict, ITaskLocationsDictPostBody} from '../../shared/types/taskLocationsDict.types';
import {IReasonRejectionsDictPostBody} from '../../shared/types/reasonRejectionsDict.types';

import {IDeviceModel} from '../../shared/types/deviceModels.types';
import {IDevice} from '../../shared/types/device.types';
import {ServiceTypesDictDto} from '../../shared/types/api-v2.types';

export const DictionaryService = {
  async getAllDeviceTypes(
    attr: {
      pageable?: IPageable;
      spec?: any;
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<IDeviceTypesDict>>(getDictionaryUrl('/service/types'), {
      params: {
        ...attr.pageable,
        ...attr.spec,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async getMyDeviceTypes() {
    const res = await axiosInstance.get<ServiceTypesDictDto[]>(getDictionaryUrl('/service/types/my'));
    return res;
  },

  async getDeviceTypeByCode(code: string) {
    const res = await axiosInstance.get<IDeviceTypesDict>(getDictionaryUrl(`/service/types/${code}`));

    return res;
  },

  async updateDeviceType(data: IDeviceTypesDict) {
    const updated = await axiosInstance.put<IDeviceTypesDict>(getDictionaryUrl(`/service/types`), data);

    return updated;
  },

  async deleteDeviceType(code: string) {
    await axiosInstance.delete(getDictionaryUrl(`/${code}`));
  },

  async createDeviceType(data: IDeviceTypesDictPostBody) {
    const created = await axiosInstance.post<IDeviceTypesDict>(getDictionaryUrl('/service/types'), data);

    return created;
  },

  async getAllTaskLocations(
    attr: {
      pageable?: IPageable;
      spec?: any;
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<ITaskLocationsDict>>(getDictionaryUrl('/contract/locations'), {
      params: {
        ...attr.pageable,
        ...attr.spec,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async getTaskLocationByCode(code: string) {
    const res = await axiosInstance.get<ITaskLocationsDict>(getDictionaryUrl(`/contract/locations`), {
      params: {
        code,
      },
    });

    return res;
  },

  async updateTaskLocation(code: string, data: ITaskLocationsDict) {
    const updated = await axiosInstance.put<ITaskLocationsDict>(getDictionaryUrl(`/contract/locations`), {
      ...data,
      code,
    });

    return updated;
  },

  async deleteTaskLocation(code: string) {
    await axiosInstance.delete(getDictionaryUrl(`/contract/locations`), {
      data: [code],
    });
  },

  async createTaskLocation(data: ITaskLocationsDictPostBody) {
    const created = await axiosInstance.post<ITaskLocationsDict>(getDictionaryUrl('/contract/locations'), data);

    return created;
  },

  async getAllReasonRejections(
    attr: {
      pageable?: IPageable;
      spec?: any;
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<IReasonRejectionsDict>>(getDictionaryUrl('/sr/rejections'), {
      params: {
        ...attr.pageable,
        ...attr.spec,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async createReasonRejection(data: IReasonRejectionsDictPostBody) {
    const created = await axiosInstance.post<IReasonRejectionsDict>(getDictionaryUrl('/sr/rejections'), data);

    return created;
  },

  async getReasonRejectionByCode(code: string) {
    const res = await axiosInstance.get<IReasonRejectionsDict>(getDictionaryUrl(`/sr/rejections`), {
      params: {
        code,
      },
    });

    return res;
  },

  async updateReasonRejection(code: string, data: IReasonRejectionsDict) {
    const updated = await axiosInstance.put<IReasonRejectionsDict>(getDictionaryUrl(`/sr/rejections`), {...data, code});

    return updated;
  },

  async deleteReasonRejection(code: string) {
    await axiosInstance.delete(getDictionaryUrl(`/sr/rejections`), {
      params: {
        code,
      },
    });
  },

  async getAllDeviceModels(
    attr: {
      pageable?: IPageable;
      spec?: {modelName: string};
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<IDeviceModel>>(getModelsUrl(), {
      params: {
        ...attr.pageable,
        ...attr.spec,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async createDeviceModel(data: Omit<IDeviceModel, 'id'>) {
    const res = await axiosInstance.post<unknown>(getModelsUrl('/create'), data);

    return res;
  },

  async updateDeviceModel(data: IDeviceModel) {
    const updated = await axiosInstance.put<unknown>(getModelsUrl(), data);

    return updated;
  },

  async getAllDevices(
    attr: {
      pageable?: IPageable;
      spec?: {
        id?: number;
        serial?: string;
        operator?: string;
        modelName?: string;
        createdYear?: number;
      };
    } = {},
  ) {
    const res = await axiosInstance.get<IPageableDto<IDevice>>(getDevicesUrl(), {
      params: {
        ...attr.pageable,
        ...attr.spec,
      },
      paramsSerializer: {
        serialize: (params) => {
          return qs.stringify(params, {arrayFormat: 'repeat'});
        },
      },
    });

    return res;
  },

  async updateDevice(data: IDevice) {
    const updated = await axiosInstance.put<unknown>(getDevicesUrl(), data, {
      params: {
        id: data.id,
      },
    });

    return updated;
  },

  async verifyDevice(id: number) {
    const res = await axiosInstance.get<unknown>(getDevicesUrl('/verify'), {
      params: {
        id,
      },
    });

    return res;
  },
};
